import { ResponsePostResendBookingEmailPresenter } from "@/libs/server/newGoApi/api/history/historyServiceInterface/postResendBookingEmailIbterface";
import {
  ProductTypeEnums,
  RequestListProductTypeEnums,
} from "@/types/product/account/history";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";

import {
  type RequestGetCheckCancelStatusInterface,
  ResponseGetCancelFeePresenter,
} from "./historyServiceInterface/getHistoryBookingInterface";
import { ResponseGetDetailCarRentalPresenter } from "./historyServiceInterface/getHistoryCarRentalDetailInterface";
import { ResponseGetHistoryCarRentalListPresenter } from "./historyServiceInterface/getHistoryCarRentalListInterface";
import { ResponseGetDetailEventPresenter } from "./historyServiceInterface/getHistoryEventDetailInterface";
import { ResponseGetHistoryEventListPresenter } from "./historyServiceInterface/getHistoryEventListInterface";
import { ResponseGetDetailExperiencePresenter } from "./historyServiceInterface/getHistoryExperienceDetailInterface";
import { ResponseGetHistoryExperienceListPresenter } from "./historyServiceInterface/getHistoryExperienceListInterface";
import { ResponseGetDetailFlightPresenter } from "./historyServiceInterface/getHistoryFlightDetailInterface";
import { ResponseGetHistoryFlightListPresenter } from "./historyServiceInterface/getHistoryFlightListInterface";
import { ResponseGetDetailHotelPresenter } from "./historyServiceInterface/getHistoryHotelDetailInterface";
import { ResponseGetHistoryHotelListPresenter } from "./historyServiceInterface/getHistoryHotelListInterface";
import {
  type RequestCancelBookingInterface,
  ResponseCancelBookingPresenter,
} from "./historyServiceInterface/postCancelBooking";
import {
  type RequestCrossSaleInterface,
  ResponseCrossSalePresenter,
} from "./historyServiceInterface/postCrossSaleInterface";
import {
  type RequestSearchBookingInterface,
  SearchBookingDataPresenter,
} from "./historyServiceInterface/postSearchBookingInterface";
import type { RequestSendEmailBookingInterface } from "./historyServiceInterface/postSendEmail";
import type { RequestPostUpdateContactInterface } from "./historyServiceInterface/postUpdateContactInterface";
import { ResponsePostUpdateContactInterface } from "./historyServiceInterface/postUpdateContactInterface";

class HistoryBookingServiceApi {
  private baseUrl: string;

  private basePathV2: string;

  private basePathV4: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePathV2 = "v2";
    this.basePathV4 = "v4/history";
  }

  // ******************************************************
  // ****************** Method: Get ***********************
  // ******************************************************

  async getHistory(params: { type: string; isLanding?: number }) {
    const { type, isLanding } = params;
    const request = {
      limit: 100,
      page: 1,
      type,
      isLanding: isLanding ?? 0,
    };
    try {
      const queryParams = new URLSearchParams(request as any);
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV4}/history?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );

      switch (type) {
        case RequestListProductTypeEnums.FLIGHT:
          return new ResponseGetHistoryFlightListPresenter(response);
        case RequestListProductTypeEnums.HOTEL:
          return new ResponseGetHistoryHotelListPresenter(response);
        case RequestListProductTypeEnums.CAR:
          return new ResponseGetHistoryCarRentalListPresenter(response);
        case RequestListProductTypeEnums.EXPERIENCE:
          return new ResponseGetHistoryExperienceListPresenter(response);
        case RequestListProductTypeEnums.EVENT:
          return new ResponseGetHistoryEventListPresenter(response);
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  async getDetail(params: { bookingInternalNo: string; type?: string }) {
    const { bookingInternalNo, type } = params;
    try {
      const queryParams = new URLSearchParams({ bookingInternalNo } as any);
      const response: any = await apiRequest(
        this.baseUrl,
        `${this.basePathV4}/detail?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
      switch (type) {
        case ProductTypeEnums.FLIGHT:
          return new ResponseGetDetailFlightPresenter(response);
        case ProductTypeEnums.HOTEL:
          return new ResponseGetDetailHotelPresenter(response);
        case ProductTypeEnums.CARRENTAL:
          return new ResponseGetDetailCarRentalPresenter(response);
        case ProductTypeEnums.EXPERIENCE: {
          if (response && response?.data?.type === ProductTypeEnums.EVENT) {
            return new ResponseGetDetailEventPresenter(response);
          }
          return new ResponseGetDetailExperiencePresenter(response);
        }
        default:
          return null;
      }
    } catch (e) {
      // throw e;
      return null;
    }
  }

  async getCancelFee(params: RequestGetCheckCancelStatusInterface) {
    try {
      const queryParams = new URLSearchParams(params as any);
      const response = await apiRequest(
        this.baseUrl,
        `v2/hotel/CancelFee?${queryParams}`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
      return new ResponseGetCancelFeePresenter(response);
    } catch {
      return new ResponseGetCancelFeePresenter({});
    }
  }

  // ******************************************************
  // ****************** Method: Post ***********************
  // ******************************************************

  async cancelBooking(params: RequestCancelBookingInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `v2/hotel/cancel`,
        "POST",
        params,
      );
      return new ResponseCancelBookingPresenter(response);
    } catch {
      return new ResponseCancelBookingPresenter({});
    }
  }

  async sendDetailBookingEmail(params: RequestSendEmailBookingInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `v2/payment/booking/resendBookingEmail`,
        "POST",
        params,
      );
      return new ResponsePostResendBookingEmailPresenter(response);
    } catch {
      return new ResponsePostResendBookingEmailPresenter({});
    }
  }

  async postCancelUnpaidBooking(params: RequestCancelBookingInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV4}/cancel`,
        "POST",
        params,
      );
      return new ResponseCancelBookingPresenter(response);
    } catch {
      return new ResponseCancelBookingPresenter({});
    }
  }

  async postUpdateContact(params: RequestPostUpdateContactInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV4}/updateContact`,
        "POST",
        params,
      );
      return new ResponsePostUpdateContactInterface(response);
    } catch (e) {
      return new ResponsePostUpdateContactInterface({});
    }
  }

  async postSearchBooking(params: RequestSearchBookingInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV4}/searchbooking`,
        "POST",
        params,
      );
      const { search } = new SearchBookingDataPresenter(response);
      switch (search) {
        case ProductTypeEnums.FLIGHT:
          return new ResponseGetDetailFlightPresenter(response);
        case ProductTypeEnums.HOTEL:
          return new ResponseGetDetailHotelPresenter(response);
        case ProductTypeEnums.CAR:
          return new ResponseGetDetailCarRentalPresenter(response);
        case ProductTypeEnums.EXPERIENCE:
          return new ResponseGetDetailExperiencePresenter(response);
        case ProductTypeEnums.EVENT:
          return new ResponseGetDetailEventPresenter(response);
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  async postCrossSale(params: RequestCrossSaleInterface) {
    try {
      const response = await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/promotion/crossSell`,
        "POST",
        // { bookingNo: ["472104D8D3B3F8EB5"] }, // Hotel
        params,
      );
      return new ResponseCrossSalePresenter(response);
    } catch (e) {
      return null;
    }
  }
}

export default HistoryBookingServiceApi;
